import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  FormControlLabel,
  Switch,
  ListItemText,
  Checkbox
} from '@material-ui/core';
import {
  CloudUpload,
  VideoLibrary,
  PictureAsPdf,
  Schedule,
  LocalOffer,
  Add,
  Close,
  PlayArrow,
  GetApp
} from '@material-ui/icons';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import { 
  getActiveTags, 
  createTag, 
  TAG_COLORS 
} from '../../services/tagService_new';
import './VideoUploadFormNew.scss';

const VideoUploadForm = ({ open, onClose, onSave, editingVideo }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    requiredRank: '',
    duration: '',
    lessons: 1,
    courseId: '',
    tags: [],
    isActive: true,
    order: 0
  });

  const [videoFile, setVideoFile] = useState(null);
  const [ebookFile, setEbookFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [ebookUrl, setEbookUrl] = useState('');
  const [videoProgress, setVideoProgress] = useState(0);
  const [ebookProgress, setEbookProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [showNewTagForm, setShowNewTagForm] = useState(false);
  const [videoDuration, setVideoDuration] = useState('');
  const [detectedDuration, setDetectedDuration] = useState(false);

  const fileInputRef = useRef(null);
  const ebookInputRef = useRef(null);

  const ranks = [
    { value: 'basic', label: 'Basic', color: '#6B7280' },
    { value: 'premier', label: 'Premier', color: '#059669' },
    { value: 'silver', label: 'Silver', color: '#6B7280' },
    { value: 'gold', label: 'Gold', color: '#D97706' },
    { value: 'platinum', label: 'Platinum', color: '#7C3AED' },
    { value: 'diamond', label: 'Diamond', color: '#3B82F6' },
    { value: 'blue_diamond', label: 'Blue Diamond', color: '#0EA5E9' },
    { value: 'presidential_diamond', label: 'Presidential Diamond', color: '#EF4444' }
  ];

  const categories = [
    'Început',
    'Echipă',
    'Avansați',
    'Wellness',
    'Business',
    'Produse',
    'Testimoniale',
    'Q&A'
  ];

  useEffect(() => {
    if (open) {
      loadTags();
      if (editingVideo) {
        // Ensure all required fields have values when editing
        setFormData({
          title: editingVideo.title || '',
          description: editingVideo.description || '',
          category: editingVideo.category || '',
          requiredRank: editingVideo.requiredRank || '',
          duration: editingVideo.duration || '',
          lessons: editingVideo.lessons || 1,
          courseId: editingVideo.courseId || '',
          tags: editingVideo.tags || [],
          isActive: editingVideo.isActive !== undefined ? editingVideo.isActive : true,
          order: editingVideo.order || 0
        });
        setVideoUrl(editingVideo.videoUrl || '');
        setEbookUrl(editingVideo.ebookUrl || '');
      } else {
        resetForm();
      }
    }
  }, [open, editingVideo]);

  const loadTags = async () => {
    try {
      console.log('Loading tags...');
      const tags = await getActiveTags();
      console.log('Tags received:', tags);
      
      if (Array.isArray(tags)) {
        setAvailableTags(tags);
        console.log('Tags set successfully:', tags.length, 'tags');
      } else {
        console.warn('Tags response is not an array:', tags);
        setAvailableTags([]);
      }
    } catch (error) {
      console.error('Error loading tags:', error);
      setAvailableTags([]); // Set to empty array on error
      toast.error('Eroare la încărcarea tag-urilor. Se va folosi lista goală.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      requiredRank: '',
      duration: '',
      lessons: 1,
      courseId: '',
      tags: [],
      isActive: true,
      order: 0
    });
    setVideoFile(null);
    setEbookFile(null);
    setVideoUrl('');
    setEbookUrl('');
    setVideoProgress(0);
    setEbookProgress(0);
    setVideoDuration('');
    setDetectedDuration(false);
  };

  const handleVideoSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('video/')) {
      setVideoFile(file);
      detectVideoDuration(file);
    } else {
      toast.error('Te rugăm să selectezi un fișier video valid');
    }
  };

  const handleEbookSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setEbookFile(file);
    } else {
      toast.error('Te rugăm să selectezi un fișier PDF valid');
    }
  };

  const detectVideoDuration = (file) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      const duration = video.duration;
      const minutes = Math.floor(duration / 60);
      const seconds = Math.floor(duration % 60);
      const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      
      setVideoDuration(formattedDuration);
      setFormData(prev => ({ ...prev, duration: formattedDuration }));
      setDetectedDuration(true);
      
      toast.success(`Durată detectată automat: ${formattedDuration}`);
      window.URL.revokeObjectURL(video.src);
    };
    
    video.src = URL.createObjectURL(file);
  };

  const uploadFile = async (file, folder) => {
    const storage = getStorage();
    const fileExtension = file.name.split('.').pop();
    const fileName = `${folder}/${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExtension}`;
    const storageRef = ref(storage, fileName);
    
    const uploadTask = uploadBytesResumable(storageRef, file);
    
    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (folder === 'library') {
            setVideoProgress(progress);
          } else {
            setEbookProgress(progress);
          }
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Titlul este obligatoriu');
      return;
    }

    if (!videoFile && !videoUrl) {
      toast.error('Te rugăm să încărci un video');
      return;
    }

    setUploading(true);

    try {
      let finalVideoUrl = videoUrl;
      let finalEbookUrl = ebookUrl;

      // Upload video if file is selected
      if (videoFile) {
        toast.info('Se încarcă videoclipul...');
        finalVideoUrl = await uploadFile(videoFile, 'library');
      }

      // Upload eBook if file is selected
      if (ebookFile) {
        toast.info('Se încarcă eBook-ul...');
        finalEbookUrl = await uploadFile(ebookFile, 'library');
      }

      const videoData = {
        ...formData,
        videoUrl: finalVideoUrl,
        ebookUrl: finalEbookUrl,
        id: editingVideo?.id || `video_${Date.now()}`
      };

      await onSave(videoData);
      toast.success(editingVideo ? 'Video actualizat cu succes!' : 'Video adăugat cu succes!');
      onClose();
    } catch (error) {
      console.error('Error saving video:', error);
      toast.error('Eroare la salvarea videoclipului');
    } finally {
      setUploading(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTagName?.trim()) {
      toast.warning('Introduceți numele pentru tag');
      return;
    }

    try {
      console.log('Creating new tag:', newTagName.trim());
      
      // Ensure TAG_COLORS is available
      const colors = TAG_COLORS && Array.isArray(TAG_COLORS) && TAG_COLORS.length > 0 
        ? TAG_COLORS 
        : ['#e3f2fd', '#f3e5f5', '#e8f5e8', '#fff3e0', '#ffebee'];
      
      const newTag = await createTag({
        name: newTagName.trim(),
        category: 'general',
        color: colors[Math.floor(Math.random() * colors.length)]
      });

      if (newTag && newTag.id) {
        setAvailableTags(prev => {
          const currentTags = Array.isArray(prev) ? prev : [];
          return [...currentTags, newTag];
        });
        
        setFormData(prev => ({
          ...prev,
          tags: [...(Array.isArray(prev.tags) ? prev.tags : []), newTag.id]
        }));
        
        setNewTagName('');
        setShowNewTagForm(false);
        toast.success('Tag nou adăugat cu succes!');
        console.log('Tag created successfully:', newTag);
      } else {
        throw new Error('Tag creation returned invalid response');
      }
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error('Eroare la adăugarea tag-ului: ' + (error.message || 'Eroare necunoscută'));
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      className="video-upload-dialog"
    >
      <div className="glass-dialog-container">
        <div className="glass-dialog-header">
          <div className="header-icon-wrapper">
            <VideoLibrary className="header-icon" />
          </div>
          <div className="header-content">
            <h2 className="dialog-title">
              {editingVideo ? 'Editează Video' : 'Adaugă Video Nou'}
            </h2>
            <p className="dialog-subtitle">
              Încarcă și configurează videoclipul pentru biblioteca ta
            </p>
          </div>
          <button 
            className="close-button-modern" 
            onClick={onClose}
            disabled={uploading}
          >
            <Close />
          </button>
        </div>

        <div className="glass-dialog-content">
          <div className="content-grid">
            {/* Basic Information */}
            <div className="glass-card">
              <div className="card-header">
                <h3 className="card-title">Informații de bază</h3>
              </div>
              <div className="card-content">
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label className="form-label">Titlu *</label>
                    <input
                      type="text"
                      className="glass-input"
                      value={formData.title || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({ ...prev, title: value }));
                      }}
                      disabled={uploading}
                      placeholder="Introdu titlul videoclipului"
                    />
                  </div>
                  
                  <div className="form-group full-width">
                    <label className="form-label">Descriere *</label>
                    <textarea
                      className="glass-textarea"
                      value={formData.description || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({ ...prev, description: value }));
                      }}
                      disabled={uploading}
                      placeholder="Descrierea videoclipului"
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Categorie</label>
                    <select
                      className="glass-select"
                      value={formData.category || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({ ...prev, category: value }));
                      }}
                      disabled={uploading}
                    >
                      <option value="">Selectează categoria</option>
                      {categories.map(category => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Rang Necesar</label>
                    <select
                      className="glass-select"
                      value={formData.requiredRank || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({ ...prev, requiredRank: value }));
                      }}
                      disabled={uploading}
                    >
                      <option value="">Selectează rangul</option>
                      {ranks.map(rank => (
                        <option key={rank.value} value={rank.value}>
                          {rank.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Video Upload */}
            <Grid item xs={12}>
              <Card className="glass-card">
                <CardContent>
                  <Typography variant="h6" className="section-title">
                    <VideoLibrary className="section-icon" />
                    Video
                  </Typography>
                  
                  <div className="upload-section">
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoSelect}
                      style={{ display: 'none' }}
                      ref={fileInputRef}
                      disabled={uploading}
                    />
                    
                    <Button
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      onClick={() => fileInputRef.current.click()}
                      className="upload-button glass-button"
                      disabled={uploading}
                    >
                      Încarcă Video
                    </Button>
                    
                    {videoFile && (
                      <div className="file-info">
                        <Typography variant="body2">
                          Fișier selectat: {videoFile.name}
                        </Typography>
                        {detectedDuration && (
                          <Typography variant="body2" className="duration-info">
                            <Schedule className="duration-icon" />
                            Durată detectată: {videoDuration}
                          </Typography>
                        )}
                      </div>
                    )}
                    
                    {videoProgress > 0 && videoProgress < 100 && (
                      <div className="upload-progress">
                        <Typography variant="body2">
                          Încărcare video: {Math.round(videoProgress)}%
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={videoProgress} 
                          className="glass-progress"
                        />
                      </div>
                    )}
                    
                    {videoUrl && (
                      <div className="preview-section">
                        <Typography variant="body2" className="preview-label">
                          <PlayArrow /> Video încărcat cu succes
                        </Typography>
                      </div>
                    )}
                  </div>

                  <Grid container spacing={2} style={{ marginTop: 16 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Durată (detectată automat)"
                        value={formData.duration || ''}
                        InputProps={{
                          readOnly: detectedDuration,
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData(prev => ({ ...prev, duration: value }));
                        }}
                        className="glass-input"
                        disabled={uploading || detectedDuration}
                        helperText={detectedDuration ? "Detectată automat din video" : "Format: MM:SS"}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Număr Lecții"
                        value={formData.lessons || 1}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          setFormData(prev => ({ ...prev, lessons: value }));
                        }}
                        className="glass-input"
                        disabled={uploading}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* eBook Upload */}
            <Grid item xs={12}>
              <Card className="glass-card">
                <CardContent>
                  <Typography variant="h6" className="section-title">
                    <PictureAsPdf className="section-icon" />
                    eBook (Opțional)
                  </Typography>
                  
                  <div className="upload-section">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleEbookSelect}
                      style={{ display: 'none' }}
                      ref={ebookInputRef}
                      disabled={uploading}
                    />
                    
                    <Button
                      variant="outlined"
                      startIcon={<GetApp />}
                      onClick={() => ebookInputRef.current.click()}
                      className="upload-button glass-button"
                      disabled={uploading}
                    >
                      Încarcă eBook PDF
                    </Button>
                    
                    {ebookFile && (
                      <div className="file-info">
                        <Typography variant="body2">
                          eBook selectat: {ebookFile.name}
                        </Typography>
                      </div>
                    )}
                    
                    {ebookProgress > 0 && ebookProgress < 100 && (
                      <div className="upload-progress">
                        <Typography variant="body2">
                          Încărcare eBook: {Math.round(ebookProgress)}%
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={ebookProgress} 
                          className="glass-progress"
                        />
                      </div>
                    )}
                    
                    {ebookUrl && (
                      <div className="preview-section">
                        <Typography variant="body2" className="preview-label">
                          <PictureAsPdf /> eBook încărcat cu succes
                        </Typography>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>

            {/* Tags */}
            <Grid item xs={12}>
              <Card className="glass-card">
                <CardContent>
                  <Typography variant="h6" className="section-title">
                    <LocalOffer className="section-icon" />
                    Tag-uri
                  </Typography>
                  
                  <FormControl fullWidth className="glass-input">
                    <InputLabel>Tag-uri</InputLabel>
                    <Select
                      multiple
                      value={formData.tags || []}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({ ...prev, tags: value }));
                      }}
                      renderValue={(selected) => (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {(selected || []).map((tagId) => {
                            const tag = (availableTags || []).find(t => t.id === tagId);
                            return tag ? (
                              <Chip
                                key={tagId}
                                size="small"
                                label={tag.name}
                                style={{ 
                                  backgroundColor: tag.color || '#e3f2fd',
                                  color: '#333',
                                  fontWeight: 500
                                }}
                              />
                            ) : null;
                          })}
                        </div>
                      )}
                      disabled={uploading}
                    >
                      {(availableTags || []).map((tag) => (
                        <MenuItem key={tag.id} value={tag.id}>
                          <Checkbox 
                            checked={(formData.tags || []).includes(tag.id)} 
                            color="primary" 
                          />
                          <ListItemText 
                            primary={tag.name} 
                            style={{ color: tag.color || '#333' }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <div className="new-tag-section">
                    {!showNewTagForm ? (
                      <Button
                        startIcon={<Add />}
                        onClick={() => setShowNewTagForm(true)}
                        className="add-tag-button"
                        disabled={uploading}
                      >
                        Adaugă Tag Nou
                      </Button>
                    ) : (
                      <div className="new-tag-form">
                        <TextField
                          fullWidth
                          size="small"
                          label="Nume tag nou"
                          value={newTagName || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setNewTagName(value);
                          }}
                          className="glass-input"
                          disabled={uploading}
                        />
                        <div className="new-tag-actions">
                          <Button
                            size="small"
                            onClick={handleAddTag}
                            disabled={!newTagName.trim() || uploading}
                          >
                            Adaugă
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              setShowNewTagForm(false);
                              setNewTagName('');
                            }}
                            disabled={uploading}
                          >
                            Anulează
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>

            {/* Additional Settings */}
            <Grid item xs={12}>
              <Card className="glass-card">
                <CardContent>
                  <Typography variant="h6" className="section-title">
                    Setări suplimentare
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="ID Curs (opțional)"
                        value={formData.courseId || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData(prev => ({ ...prev, courseId: value }));
                        }}
                        className="glass-input"
                        disabled={uploading}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Ordine (pentru sortare)"
                        value={formData.order || 0}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          setFormData(prev => ({ ...prev, order: value }));
                        }}
                        className="glass-input"
                        disabled={uploading}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isActive}
                            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                            color="primary"
                            disabled={uploading}
                          />
                        }
                        label="Video activ (vizibil pentru utilizatori)"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </div>
        </div>
        

        <div className="glass-dialog-footer">
          <button 
            onClick={onClose} 
            disabled={uploading}
            className="cancel-button-modern"
          >
            Anulează
          </button>
          <button 
            onClick={handleSubmit} 
            disabled={uploading || (!videoFile && !videoUrl) || !formData.title.trim()}
            className="save-button-modern"
          >
            {uploading ? 'Se salvează...' : (editingVideo ? 'Actualizează' : 'Salvează')}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default VideoUploadForm;
